import { ApartmentAssets } from '../../../api/types';

/**
 * The issue-report object picker's labels, by object id. The picker matches a
 * pick back by its label, so no two objects may share one: a room's object
 * reads the room's title, any other object its own name (the server sends
 * titles for rooms only), a label that still repeats — objects are
 * numbered per station, "Fach 1" at two stations — carries the address, and
 * one that repeats even then a number.
 */
export const issueReportPickerLabels = (
    assets: ApartmentAssets[],
    lang: 'en' | 'de',
): Map<string, string> => {
    const base = new Map(
        assets.map(asset => [asset.id, asset.langNames?.[lang]?.title ?? asset.name ?? ''] as const),
    );
    const withAddress = disambiguate(assets, base, (asset, label) =>
        asset.address ? `${label} (${asset.address})` : label,
    );
    // Two stations at one location share an address: a last numbered
    // tie-break keeps every label unique.
    return disambiguate(assets, withAddress, (_asset, label, n) => `${label} #${n}`);
};

/** Relabels every label that more than one object carries; `n` counts from 1. */
const disambiguate = (
    assets: ApartmentAssets[],
    labels: Map<string, string>,
    relabel: (asset: ApartmentAssets, label: string, n: number) => string,
): Map<string, string> => {
    const counts = new Map<string, number>();
    labels.forEach(label => counts.set(label, (counts.get(label) ?? 0) + 1));
    const seen = new Map<string, number>();
    return new Map(
        assets.map(asset => {
            const label = labels.get(asset.id) ?? '';
            if ((counts.get(label) ?? 0) < 2) return [asset.id, label];
            const n = (seen.get(label) ?? 0) + 1;
            seen.set(label, n);
            return [asset.id, relabel(asset, label, n)];
        }),
    );
};
